import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { env } from "@/lib/env";

/**
 * Untyped Supabase client (Phase 1). After migrations 0001–0006 are applied,
 * run `npm run gen:types` to emit `src/lib/database.types.ts`, then replace
 * the `any` generics below with `<Database>` and get column-level type safety.
 */
/** localStorage: `{ type: "invite" | "recovery", userId }` until that user sets a password. */
export const PENDING_PASSWORD_KEY = "neo-pending-password";

/** The `sub` (user id) claim of an access token, or null if unreadable. */
function tokenUserId(token: string | null): string | null {
  try {
    const payload = token?.split(".")[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof claims.sub === "string" ? claims.sub : null;
  } catch {
    return null;
  }
}

/*
 * Invite and password-reset emails land with `#access_token=…&type=invite`
 * (or `type=recovery`) and sign the user in without a password. The client
 * consumes and clears the hash as soon as it initialises, so read it first
 * and remember, for that user only, that they still need to set a password.
 */
const linkParams = new URLSearchParams(window.location.hash.slice(1));
const linkType = linkParams.get("type");
const linkUserId = tokenUserId(linkParams.get("access_token"));
if ((linkType === "invite" || linkType === "recovery") && linkUserId) {
  try {
    localStorage.setItem(PENDING_PASSWORD_KEY, JSON.stringify({ type: linkType, userId: linkUserId }));
  } catch {
    /* storage unavailable: the user can still use Reset later */
  }
}

/** True when the user arrived from an expired or already-used email link. */
export const authLinkFailed = linkParams.has("error_description") || linkParams.has("error_code");
if (authLinkFailed) {
  // Drop the error hash so a reload doesn't show the notice again.
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const supabase: SupabaseClient<any, "public", any> = createClient(
  env.SUPABASE_URL,
  env.SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);
